<template>
  <div class="school-video-player">
    <div :id="containerId" class="video-container"></div>
    <div v-if="!playUrl" class="video-empty">暂无视频流</div>
    <div v-else-if="loading" class="video-loading">
      <span class="loader"></span>
      <span>视频加载中</span>
    </div>
    <div v-else-if="errorMessage" class="video-error">
      <span>{{ errorMessage }}</span>
      <button type="button" @click="retry">重新连接</button>
    </div>
  </div>
</template>

<script>
import { EZUIKitPlayer } from 'ezuikit-js'

export default {
  name: 'SchoolVideoPlayer',
  props: {
    accessToken: {
      type: String,
      default: ''
    },
    videoUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      player: null,
      loading: false,
      errorMessage: '',
      containerId: `school-video-${Date.now()}-${Math.floor(Math.random() * 10000)}`
    }
  },
  computed: {
    playUrl() {
      if (!this.videoUrl) return ''
      return this.normalizePlayUrl(this.videoUrl)
    }
  },
  watch: {
    playUrl() {
      this.$nextTick(this.initPlayer)
    },
    accessToken() {
      this.$nextTick(this.initPlayer)
    }
  },
  mounted() {
    this.initPlayer()
  },
  beforeDestroy() {
    this.destroyPlayer()
  },
  methods: {
    initPlayer() {
      this.destroyPlayer()
      this.errorMessage = ''
      if (!this.playUrl) {
        this.loading = false
        return
      }
      if (!this.accessToken) {
        this.loading = false
        this.errorMessage = '直播授权信息缺失'
        return
      }
      this.loading = true
      try {
        this.player = new EZUIKitPlayer({
          id: this.containerId,
          accessToken: this.accessToken,
          url: this.playUrl,
          template: 'simple',
          width: '100%',
          height: '100%',
          audio: false,
          autoPlay: true,
          useHardDev: true,
          enableSharedArrayBufferGuide: false,
          // 与 trajectory.vue 保持一致：完全填充视频容器，避免宽屏区域两侧出现黑边。
          scaleMode: 0,
          streamInfoCBType: 1,
          env: {
            domain: 'https://open.ys7.com'
          },
          handleSuccess: () => {
            this.loading = false
            this.errorMessage = ''
          },
          handleError: (error) => {
            console.error('[SchoolVideoPlayer] EZUIKit 播放失败', error)
            this.loading = false
            this.errorMessage = this.formatPlayerError(error)
            this.$emit('error', error)
          }
        })
        this.player.eventEmitter.on(EZUIKitPlayer.EVENTS.firstFrameDisplay, () => {
          this.loading = false
          this.errorMessage = ''
        })
        this.player.eventEmitter.on(EZUIKitPlayer.EVENTS.streamInfoCB, (info) => {
          console.info('[SchoolVideoPlayer] 流信息', info)
        })
      } catch (error) {
        console.error('[SchoolVideoPlayer] EZUIKit 初始化失败', error)
        this.loading = false
        this.errorMessage = this.formatPlayerError(error)
      }
    },
    normalizePlayUrl(value) {
      let current = value
      for (let index = 0; index < 3; index += 1) {
        if (current && typeof current === 'object') {
          current = current.url || (current.data && current.data.url) || ''
          continue
        }
        if (typeof current !== 'string') return ''
        const text = current.trim()
        if (!text) return ''
        if (/^ezopen:\/\//i.test(text)) return text
        try {
          current = JSON.parse(text)
        } catch (error) {
          return text
        }
      }
      return typeof current === 'string' ? current.trim() : ''
    },
    formatPlayerError(error) {
      const type = error && error.type ? String(error.type) : ''
      const code = error && error.data && error.data.nErrorCode ? `（错误码 ${error.data.nErrorCode}）` : ''
      if (/token|auth|license/i.test(type)) return `直播授权失败${code}`
      if (/url|device|stream|play|runtime/i.test(type)) return `直播流连接失败${code}`
      return `直播初始化失败${code}`
    },
    retry() {
      this.initPlayer()
    },
    destroyPlayer() {
      if (!this.player) return
      const player = this.player
      this.player = null
      try {
        const result = player.destroy()
        if (result && typeof result.then === 'function') {
          result.catch(() => {})
        }
      } catch (e) {
        // ignore SDK teardown errors
      }
    }
  }
}
</script>

<style scoped>
.school-video-player {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--school-bg-deep, #010805);
  border: 1px solid var(--school-border, rgba(34, 197, 94, 0.24));
}

.video-container {
  width: 100%;
  height: 100%;
}

.video-empty,
.video-loading,
.video-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--school-accent-soft, #86efac);
  background: rgba(1, 8, 5, 0.9);
  font-size: 14px;
}

.video-error {
  flex-direction: column;
  gap: 12px;
  color: #f6c4c4;
}

.video-error button {
  min-width: 84px;
  height: 30px;
  padding: 0 12px;
  color: var(--school-accent-soft, #a8f4c7);
  background: rgba(35, 201, 135, 0.12);
  border: 1px solid var(--school-border-strong, rgba(115, 240, 176, 0.7));
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}

.loader {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(34, 197, 94, 0.24);
  border-top-color: var(--school-accent-bright, #4ade80);
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
